/*
 This file is part of GNU Taler
 (C) 2021-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  AbsoluteTime,
  HttpStatusCode,
  Paytos,
  TalerError,
  TalerMerchantApi,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  NotificationCardBulma,
  Time,
  useCommonPreferences,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { ErrorLoadingMerchant } from "../../../../components/ErrorLoadingMerchant.js";
import { Loading } from "../../../../components/exception/loading.js";
import {
  ConfirmModal,
  ValidBankAccount,
} from "../../../../components/modal/index.js";
import { useBackendExchanges } from "../../../../hooks/exchanges.js";
import { ListPage } from "./ListPage.js";
import { useState } from "preact/hooks";

const TALER_SCREEN_ID = 40;

interface Props {
  // onGetInfo: (id: string) => void;
  // onShowInstructions: (id: string) => void;
}

export default function ListExchanges(_p: Props): VNode {
  const result = useBackendExchanges();
  const { i18n } = useTranslationContext();
  const [showState, setShowState] = useState<
    TalerMerchantApi.ExchangeStatusDetail | undefined
  >(undefined);

  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.NotFound:
        return <div />;
      case HttpStatusCode.InternalServerError:
        return <div />;
      default: {
        assertUnreachable(result);
      }
    }
  }
  const { exchanges } = result.body;

  if (!exchanges.length) {
    return (
      <NotificationCardBulma
        notification={{
          message: i18n.str`No exchanges supported`,
          description: i18n.str`Currently the list of exchanges in the configuration is empty. Without this this server can process any payment.".`,
          type: "WARN",
        }}
      />
    );
  }
  return (
    <Fragment>
      {showState !== undefined ? (
        <ShowExchangeStatus
          e={showState}
          onCancel={() => setShowState(undefined)}
        />
      ) : undefined}
      <ListPage exchanges={exchanges} onSelect={(e) => setShowState(e)} />
    </Fragment>
  );
}

function ShowExchangeStatus({
  e,
  onCancel,
}: {
  e: TalerMerchantApi.ExchangeStatusDetail;
  onCancel: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const isWorking = e.keys_http_status >= 200 && e.keys_http_status < 300;
  if (isWorking) {
    return (
      <ConfirmModal
        label={i18n.str`Ok`}
        description={e.exchange_url as TranslatedString}
        active
        onCancel={onCancel}
      >
        <p style={{ paddingTop: 0 }}>
          {e.keys_expiration ? (
            <i18n.Translate>
              Ready until{" "}
              <Time
                format="dd/MM/yyyy HH:mm:ss"
                timestamp={AbsoluteTime.fromProtocolTimestamp(
                  e.keys_expiration,
                )}
              />
            </i18n.Translate>
          ) : undefined}
        </p>
        <p style={{ paddingTop: 0 }}>
          <i18n.Translate>
            Next update will be at{" "}
            <Time
              format="dd/MM/yyyy HH:mm:ss"
              timestamp={AbsoluteTime.fromProtocolTimestamp(e.next_download)}
            />
          </i18n.Translate>
        </p>
      </ConfirmModal>
    );
  }
  return (
    <ConfirmModal
      label={i18n.str`Error`}
      description={e.exchange_url as TranslatedString}
      active
      onCancel={onCancel}
    >
      <p style={{ paddingTop: 0 }}>
        <b><i18n.Translate>This exchange is down.</i18n.Translate></b>{" "}
        <i18n.Translate>
          Next update will be at{" "}
          <Time
            format="dd/MM/yyyy HH:mm:ss"
            timestamp={AbsoluteTime.fromProtocolTimestamp(e.next_download)}
          />
        </i18n.Translate>
      </p>
      <p style={{ paddingTop: 0 }}>
        <i18n.Translate>
          Last HTTP status was {e.keys_http_status}: {e.keys_hint} (#{e.keys_ec}
          )
        </i18n.Translate>
      </p>
    </ConfirmModal>
  );
}
